import { Button, Divider, Modal, Select, Spin, Table, Typography } from 'antd';
import { QrcodeOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { getViewKinSimDetailsColumns } from 'constants/table';
import { useGetKinSimCampaignDetails, useGetKinSimUrls } from 'hooks/useGetServices';
import useModal from 'stores/useModal';
import {
  SerivceKinSimUrlsDetailsType,
  StatusFilter,
  VIEW_ESIM_DETAILS_STATUS_OPTIONS
} from 'services/api/type/service.type';

import 'styles/view-esim-details.scss';

const { Text, Title } = Typography;

interface StatusCounts {
  reserved: number;
  claimed: number;
  redeemed: number;
}

function ViewKinSimDetailsModal() {
  const { t } = useTranslation();
  const {
    isOpenViewKinSimDetails,
    setIsOpenViewKinSimDetails,
    service_id_view,
    service_qty_view,
    isOpenIssueKinSim,
    setIsOpenIssueKinSim,
    campaign_id_view
  } = useModal();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(StatusFilter.ALL);
  const [qrPreview, setQrPreview] = useState<{ url: string; title?: string } | null>(null);

  // Reset status filter when modal opens/closes
  useEffect(() => {
    if (!isOpenViewKinSimDetails) {
      setStatusFilter(StatusFilter.ALL);
    }
  }, [isOpenViewKinSimDetails]);

  const enabled = isOpenViewKinSimDetails && !!service_id_view;
  const { data, isLoading, isFetching } = useGetKinSimUrls(service_id_view || 0, enabled);
  const {
    data: campaignDetails,
    isLoading: isLoadingCampaignDetails,
    isFetching: isFetchingCampaignDetails
  } = useGetKinSimCampaignDetails(data?.campaign_id, !!data?.campaign_id);

  // Combine filtering and counting into a single operation
  const { filteredUrls, statusCounts } = useMemo(() => {
    if (!data?.urls) {
      return {
        filteredUrls: [],
        statusCounts: { reserved: 0, claimed: 0, redeemed: 0 }
      };
    }

    const counts: StatusCounts = { reserved: 0, claimed: 0, redeemed: 0 };
    const withoutAvailable: typeof data.urls = [];

    data.urls.forEach(url => {
      const status = url.status?.toLowerCase();
      if (status === 'reserved') {
        counts.reserved++;
      } else {
        if (status === 'claimed') counts.claimed++;
        if (status === 'redeemed') counts.redeemed++;
        withoutAvailable.push(url);
      }
    });

    const filtered =
      statusFilter === StatusFilter.ALL
        ? withoutAvailable
        : withoutAvailable.filter(u => u.status?.toLowerCase() === statusFilter);

    return { filteredUrls: filtered, statusCounts: counts };
  }, [data, statusFilter]);

  const handleCopyUrl = useCallback((url: string) => {
    navigator.clipboard.writeText(url).catch(() => {});
  }, []);

  const handlePreviewQr = useCallback((url: string, title?: string) => {
    setQrPreview({ url, title });
  }, []);

  const handleCloseQrPreview = useCallback(() => {
    setQrPreview(null);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpenViewKinSimDetails(false);
  }, [setIsOpenViewKinSimDetails]);

  const handleIssueKinSim = useCallback(() => {
    const remainingQty = service_qty_view - (data?.total || 0);
    setIsOpenIssueKinSim(true, service_id_view, campaign_id_view, remainingQty);
  }, [service_qty_view, setIsOpenIssueKinSim, service_id_view, campaign_id_view, data?.total]);

  const showIssueKinSimButton =
    !!data && (service_qty_view !== data.total || service_qty_view > data.total);

  const columns = useMemo(
    () => getViewKinSimDetailsColumns(t, handleCopyUrl, handlePreviewQr),
    [t, handleCopyUrl, handlePreviewQr]
  );

  const renderSummary = (details: SerivceKinSimUrlsDetailsType) => (
    <div className="view-esim-details__summary">
      <div className="view-esim-details__campaign-info">
        <Text className="view-esim-details__campaign-label" type="secondary">
          {t('esim.viewDetailsModal.campaignName')}
        </Text>
        <Title level={5} className="view-esim-details__campaign-name">
          {campaignDetails?.name || '—'}
        </Title>
      </div>

      <Divider className="view-esim-details__divider" />

      <div className="view-esim-details__metrics-grid">
        <div className="view-esim-details__metric-card">
          <div className="view-esim-details__metric-value">{details.total}</div>
          <Text className="view-esim-details__metric-label" type="secondary">
            {t('esim.viewDetailsModal.totalUrls')}
          </Text>
        </div>

        <Button
          className="view-esim-details__metric-card view-esim-details__metric-card--clickable"
          disabled={statusCounts.reserved === 0}
          onClick={() =>
            handlePreviewQr(
              details.shared_link_url,
              t('esim.viewDetailsModal.qrCode', { defaultValue: 'QR Code' }) +
                ' - ' +
                t('esim.viewDetailsModal.sharedLink', { defaultValue: 'Shared' })
            )
          }
        >
          <div className="view-esim-details__metric-value">{statusCounts.reserved}</div>
          <Text className="view-esim-details__metric-label" type="secondary">
            {t('esim.viewDetailsModal.reservedCount')}
          </Text>
          {statusCounts.reserved > 0 && (
            <div className="view-esim-details__metric-action">
              <QrcodeOutlined />
              <span>View QR</span>
            </div>
          )}
        </Button>

        <div className="view-esim-details__metric-card">
          <div className="view-esim-details__metric-value">{statusCounts.claimed}</div>
          <Text className="view-esim-details__metric-label" type="secondary">
            {t('esim.viewDetailsModal.claimedCount')}
          </Text>
        </div>

        <div className="view-esim-details__metric-card">
          <div className="view-esim-details__metric-value">{statusCounts.redeemed}</div>
          <Text className="view-esim-details__metric-label" type="secondary">
            {t('esim.viewDetailsModal.redeemedCount')}
          </Text>
        </div>
      </div>
    </div>
  );

  const isLoadingData =
    isLoading || isFetching || isLoadingCampaignDetails || isFetchingCampaignDetails;

  const renderContent = () => {
    if (!enabled) return null;

    if (isLoadingData) {
      return (
        <div className="view-esim-details__loading">
          <Spin size="large" tip={t('common.loading')} />
        </div>
      );
    }

    if (!data) {
      return (
        <div className="view-esim-details__empty">
          <Text type="secondary">{t('esim.viewDetailsModal.noData')}</Text>
        </div>
      );
    }

    return (
      <>
        <div className="view-esim-details__content-header">
          <div className="view-esim-details__filter-section">
            <Text className="view-esim-details__filter-label">
              {t('esim.viewDetailsModal.filterByStatus')}
            </Text>

            <Select<StatusFilter>
              value={statusFilter}
              onChange={setStatusFilter}
              options={VIEW_ESIM_DETAILS_STATUS_OPTIONS.map(opt => ({
                value: opt.value,
                label: t(opt.labelKey)
              }))}
              style={{ width: 160 }}
            />
            {showIssueKinSimButton && (
              <Button className="ant-btn-secondary" onClick={handleIssueKinSim}>
                {t('esim.issueButton')}
              </Button>
            )}
          </div>
        </div>
        {renderSummary(data)}

        <div className="view-esim-details__table-container">
          <Table
            size="small"
            rowKey={(_, i) => `${i}`}
            columns={columns}
            dataSource={filteredUrls}
            pagination={false}
            scroll={{ x: 400, y: 'calc(96vh - 450px)' }}
            locale={{
              emptyText: <span className="empty-data">{t('common.actions.noData')}</span>
            }}
            className="view-esim-details__table"
          />
        </div>
      </>
    );
  };

  return (
    <>
      <Modal
        title={
          <div className="view-esim-details__modal-title">{t('esim.viewDetailsModal.title')}</div>
        }
        className="view-esim-details-modal"
        open={isOpenViewKinSimDetails}
        onCancel={handleClose}
        footer={null}
        destroyOnClose
        width={860}
        mask={!isOpenIssueKinSim}
        centered
      >
        <div className="view-esim-details">{renderContent()}</div>
      </Modal>

      <Modal
        className="view-esim-details-qr-preview-modal"
        title={qrPreview?.title ?? t('esim.viewDetailsModal.qrCode', { defaultValue: 'QR Code' })}
        open={!!qrPreview}
        onCancel={handleCloseQrPreview}
        footer={
          <Button type="primary" onClick={handleCloseQrPreview}>
            {t('common.close', { defaultValue: 'Close' })}
          </Button>
        }
        width={360}
        centered
        destroyOnClose
      >
        {qrPreview && (
          <div className="view-esim-details__qr-preview">
            <QRCodeSVG value={qrPreview.url} size={256} level="M" />
          </div>
        )}
      </Modal>
    </>
  );
}

export default ViewKinSimDetailsModal;
